/**
 * @fileoverview Page "Bon à savoir" du portail citoyen.
 *
 * Cette page explique le parcours public et répond aux questions fréquentes.
 * Elle reste UI-only : aucun appel backend, aucune donnée réelle, aucun lien
 * vers des documents non disponibles.
 */

import { Link } from 'react-router-dom';
import { Button, FaqList } from '@gnpc/ui';
import { HelpCircle, LockKeyhole } from 'lucide-react';

interface JourneyStep {
  title: string;
  description: string;
}

const JOURNEY_STEPS: readonly JourneyStep[] = [
  {
    title: "S'identifier",
    description:
      'Vous utilisez le numéro de téléphone lié à votre compte Quipux et un code à usage unique.',
  },
  {
    title: 'Rechercher la plaque',
    description: 'La recherche affiche les contraventions trouvées pour la plaque saisie.',
  },
  {
    title: 'Payer',
    description: 'Vous choisissez votre moyen de paiement sur une page de paiement sécurisée.',
  },
  {
    title: 'Récupérer les preuves',
    description:
      'Le justificatif est remis ici après paiement confirmé ; la quittance se télécharge sur votre compte Quipux.',
  },
];

const FAQ_ITEMS = [
  {
    question: 'Pourquoi faut-il saisir le téléphone de mon compte Quipux ?',
    answer:
      'Ce numéro sert à vérifier que vous utilisez le compte Quipux lié à votre dossier. Un code à usage unique est envoyé par SMS.',
    defaultOpen: true,
  },
  {
    question: 'Que se passe-t-il si aucune contravention n’est trouvée ?',
    answer:
      "C'est un état normal. Vous pouvez vérifier la plaque saisie ou réessayer plus tard si le service de consultation est momentanément indisponible.",
  },
  {
    question: 'Où se fait le paiement mobile money ?',
    answer:
      'Le paiement se fait sur une page de paiement sécurisée. Le portail vous y redirige, puis vous revenez ici pour suivre le statut.',
  },
  {
    question: 'Quelle est la différence entre justificatif et quittance ?',
    answer:
      "Le justificatif de débit, remis par ce guichet, confirme que le paiement a été pris en compte. La quittance officielle est ensuite délivrée par Quipux : elle se télécharge depuis votre compte Quipux, après l'enregistrement chez Quipux.",
  },
  {
    question: 'Où télécharger ma quittance officielle ?',
    answer:
      "Sur votre compte Quipux, en dehors de ce guichet. Le paiement peut être confirmé avant que l'enregistrement chez Quipux soit terminé : le justificatif de débit reste disponible ici immédiatement, la quittance apparaît sur votre compte Quipux une fois l'enregistrement effectué.",
  },
  {
    question: 'Que faire si le paiement échoue ou expire ?',
    answer:
      "Aucun règlement n'est confirmé dans ce cas. Vous pouvez relancer le paiement depuis votre sélection.",
  },
  {
    question: 'Y a-t-il une limite de montant ?',
    answer:
      'Le parcours prévoit les bornes de paiement : minimum 300 F CFA et maximum 2 000 000 F CFA par paiement.',
  },
  {
    question: 'Mes données personnelles sont-elles affichées ?',
    answer:
      "Non. Le portail citoyen évite d'afficher des informations nominatives sur les écrans publics.",
  },
] as const;

export function GoodToKnowPage(): JSX.Element {
  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <section className="border-quipux-navy-900 bg-quipux-navy-950 overflow-hidden rounded-lg border">
        <div className="grid gap-8 p-6 text-white md:grid-cols-[minmax(0,1fr)_320px] md:items-center md:p-8 lg:p-10">
          <header className="space-y-3">
            <p className="text-quipux-orange-400 text-sm font-medium">Bon à savoir</p>
            <h1 className="text-3xl font-semibold tracking-tight">
              Comprendre le paiement de vos amendes et formalités
            </h1>
            <p className="max-w-xl text-sm leading-6 text-white/70">
              Une explication simple du parcours : identification, recherche, paiement, retour au
              portail et disponibilité des documents.
            </p>
          </header>

          <div className="rounded-lg border border-white/15 bg-white/10 p-3">
            <div className="aspect-video overflow-hidden rounded-md bg-black">
              <iframe
                className="h-full w-full"
                src="https://www.youtube.com/embed/Y_bQrRnxPHw"
                title="Vidéo explicative du parcours de paiement"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
            <p className="mt-3 text-sm font-semibold">Vidéo explicative</p>
            <p className="mt-1 text-xs leading-5 text-white/65">
              Une vidéo courte pour comprendre le parcours avant de commencer.
            </p>
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <HelpCircle className="text-quipux-orange-600 size-5" />
          <h2 className="text-quipux-navy-950 text-lg font-semibold">Comment ça marche ?</h2>
        </div>
        {/* `items-start` + texte non tronqué : le contenu explicatif doit se
          * lire en entier, chaque carte prend la hauteur de son contenu. */}
        <div className="grid items-start gap-3 md:grid-cols-4">
          {JOURNEY_STEPS.map((step, index) => (
            <article key={step.title} className="border-border bg-card rounded-lg border p-4">
              <div className="flex items-center justify-between gap-3">
                {/* `bg-primary` (pas orange-500) : le numéro d'étape est un
                  * texte informatif blanc → contraste WCAG AA 4,5:1 requis. */}
                <span className="bg-primary flex size-9 items-center justify-center rounded-full font-mono text-sm font-semibold text-white">
                  {index + 1}
                </span>
              </div>
              <h3 className="text-quipux-navy-950 mt-4 text-sm font-semibold">{step.title}</h3>
              <p className="text-muted-foreground mt-1 text-sm leading-6">{step.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <LockKeyhole className="text-quipux-blue-700 size-5" />
          <h2 className="text-quipux-navy-950 text-lg font-semibold">Questions fréquentes</h2>
        </div>
        <FaqList items={FAQ_ITEMS} />
      </section>

      <section className="border-border bg-card rounded-lg border p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-quipux-navy-950 text-sm font-semibold">Prêt à commencer ?</h2>
            <p className="text-muted-foreground mt-1 text-sm leading-6">
              Identifiez-vous avec le téléphone lié à votre compte Quipux pour accéder à la
              recherche et au paiement.
            </p>
          </div>
          <Button asChild className="shrink-0">
            <Link to="/">Commencer</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
